import React, { Fragment, useState, FC, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import { useTranslation } from 'react-i18next';

export interface StayDatesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  onSelectDates: (dates: [Date | null, Date | null]) => void;
  startDate: Date | null;
  endDate: Date | null;
  isModifiable?: boolean;
}


const StayDatesRangeInput: FC<StayDatesRangeInputProps> = ({
  className = "[ lg:nc-flex-2 ]",
  fieldClassName = "[ nc-hero-field-padding ]",
  onSelectDates,
  startDate,
  endDate,
  isModifiable
}) => {
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(startDate ?? new Date());
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(endDate ?? new Date());
  //

  const { t } = useTranslation();

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setInternalStartDate(start);
    setInternalEndDate(end);
    onSelectDates(dates);
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-neutral-300 dark:text-neutral-400">
          <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left dark:text-white">
          <span className="block xl:text-md lg:text-sm font-semibold">
            {internalStartDate?.toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }) || "Ajouter dates"}
            {internalEndDate
              ? " - " +
              internalEndDate?.toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : ""}
          </span>
          <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
          {t('debutFin')}
          </span>
        </div>
      </>
    );
  };

  return (
    <Popover className={`StayDatesRangeInput relative flex ${className}`} >
      {({ open }) => (
        <>
          <Popover.Button
            className={`flex-1 z-10 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none ${
              open ? "nc-hero-field-focused" : ""
            }`}
            onClickCapture={() => document.querySelector("html")?.click()}

            disabled={!isModifiable}
          >
            {renderInput()}
            {internalStartDate && open && (
              <ClearDataButton onClick={() => onChangeDate([null, null])} />
            )}
          </Popover.Button>

          {open && (
            <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -inset-x-0.5 bg-white dark:bg-neutral-800"></div>
          )}

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute left-1/2 z-[100] mt-3 top-full w-screen max-w-sm -translate-x-1/2 transform px-4 sm:px-0 lg:max-w-3xl">
              <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">
                <DatePicker
                  selected={internalStartDate}
                  onChange={onChangeDate}
                  startDate={internalStartDate}
                  endDate={internalEndDate}
                  selectsRange
                  monthsShown={2}
                  showPopperArrow={false}
                  inline
                  renderCustomHeader={(p) => (
                    <DatePickerCustomHeaderTwoMonth {...p} />
                  )}
                  renderDayContents={(day, date) => (
                    <DatePickerCustomDay dayOfMonth={day} date={date} />
                  )}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default StayDatesRangeInput;
